"use client";

import { useEffect } from "react";

import type { Route } from "next";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

import { zodResolver } from "@hookform/resolvers/zod";
import { ROUTES } from "@unreserved/constants";
import { useLogin } from "@unreserved/hooks/use-auth";
import { useToast } from "@unreserved/hooks/use-toast";
import { type LoginFormData, loginSchema, passwordRules } from "@unreserved/schemas/auth-schemas";
import { userApi } from "@unreserved/services/sdk-client";
import { handleHttpError } from "@unreserved/utils/http-error";
import { useForm } from "react-hook-form";

import { AppButton, FormInput } from "@/app/_components/shared";
import { IMAGES } from "@/app/_libs/constants/images";
import { registerFcmToken } from "@/app/_libs/notifications";
import { useAnalytics } from "@/hooks/use-analytics";
import { useAuthStore } from "@/stores/auth-store";

import { PasswordValidationRules } from "../reset-password/password-validation-rules";
import { SocialLogin } from "./social-login";

export const LoginForm = () => {
  const { loginMutation } = useLogin();
  const { trackLogin } = useAnalytics();
  const router = useRouter();
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const setUser = useAuthStore((state) => state.setUser);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, touchedFields },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const password = watch("password");

  useEffect(() => {
    const error = searchParams.get("error");
    const message = searchParams.get("message");
    if (error && message) {
      toast.error("Login failed", { description: message });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (_data: LoginFormData) => {
    await loginMutation.mutateAsync(
      {
        email: _data.email,
        password: _data.password,
      },
      {
        onSuccess: () => {
          userApi.userControllerGetProfileV1().then((profile) => {
            const userProfile = profile.data;

            if (!userProfile) {
              return;
            }

            setUser(userProfile);
            trackLogin("email", userProfile.id);
          });
          toast.success("Login successful", {
            description: loginMutation.data?.message ?? "You have been logged in successfully",
          });
          const redirectTo = searchParams.get("redirectTo");
          const redirectUrl = redirectTo ? redirectTo : ROUTES.DASHBOARD.HOME;
          router.replace(redirectUrl as Route);
          registerFcmToken();
        },
        onError: (error: Error) => {
          handleHttpError(error, "Login failed", toast);
          console.error("Login failed:", error);
        },
      },
    );
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="relative mx-4 mt-6 h-51.25 w-auto rounded-[20px] lg:hidden">
        <Image
          src={IMAGES.LOGIN_BG}
          alt="Join Aussies Already Selling Their Own Home"
          fill
          className="rounded-[20px] object-cover"
          sizes="100vw"
          priority
        />
        <div className="absolute inset-0 rounded-xl bg-linear-to-b from-transparent to-[rgba(0,0,0,0.6)]" />
        <div className="absolute inset-0 flex flex-col items-center justify-end px-4 pb-6 text-center">
          <h1 className="text-xl leading-normal font-bold tracking-[0.2px] text-white">
            Join Aussies Already Selling Their Own Home
          </h1>
          <p className="text-typography-0 mt-1 text-xs font-normal">
            You don&apos;t have to be the first to save thousands on your home sale.
          </p>
        </div>
      </div>

      <div className="flex flex-1 items-start justify-center px-4 py-6 lg:items-center lg:justify-stretch lg:px-0 lg:py-0">
        <div className="bg-background-0 w-full rounded-lg p-4 lg:rounded-lg lg:p-10.5 lg:shadow-[0px_4px_16px_0px_rgba(38,38,38,0.12)]">
          {/* Logo and Header */}
          <div className="mb-9">
            <div className="mb-2 h-8 lg:h-13">
              <Image
                src={IMAGES.LOGO}
                alt="Unreserved Logo"
                width={130}
                height={32}
                className="lg:h-8 lg:w-44.75"
                objectFit="contain"
              />
            </div>
            <h1 className="text-tertiary-950 mb-1 text-xl font-semibold lg:mb-2 lg:text-4xl">
              Welcome Back!
            </h1>
            <p className="text-typography-600 text-xs leading-normal lg:text-sm">
              Login using the email address
            </p>
          </div>

          <form id="login-form" onSubmit={handleSubmit(onSubmit)} className="mb-6 space-y-6">
            <FormInput
              label="Email"
              type="email"
              placeholder="Enter your email"
              error={errors.email?.message}
              disabled={isSubmitting}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              }}
              {...register("email")}
            />

            <div>
              <FormInput
                label="Password"
                type="password"
                placeholder="Enter your password"
                error={
                  errors.password?.message === "Password is required"
                    ? errors.password.message
                    : undefined
                }
                disabled={isSubmitting}
                onKeyDown={(e) => {
                  if (e.key === " ") {
                    e.preventDefault();
                  }
                }}
                {...register("password")}
              />
              <PasswordValidationRules
                password={password}
                rules={passwordRules}
                touched={!!touchedFields.password}
              />
            </div>

            <div className="flex justify-end">
              <Link
                href={ROUTES.AUTH.FORGOT_PASSWORD}
                className="text-primary-700 text-xs font-medium hover:underline lg:text-sm">
                Forgot password?
              </Link>
            </div>
          </form>

          <AppButton
            type="submit"
            form="login-form"
            className="bg-primary-700 h-10 w-full cursor-pointer rounded text-sm lg:h-12 lg:rounded-lg lg:text-lg"
            text={
              <>
                <span className="lg:hidden">Login with Email</span>
                <span className="hidden lg:inline">Login</span>
              </>
            }
            isLoading={isSubmitting}
          />

          <div className="my-4 flex items-center lg:my-6">
            <div className="bg-border-200 h-px flex-1" />
            <span className="text-typography-500 px-3 text-xs font-medium lg:text-sm">
              or login with
            </span>
            <div className="bg-border-200 h-px flex-1" />
          </div>

          <SocialLogin />

          <p className="text-typography-700 mt-6 text-center text-xs lg:mt-10.5 lg:text-sm">
            <span className="font-medium">Don&apos;t have an account?</span>{" "}
            <Link
              href={ROUTES.AUTH.SIGNUP}
              className="text-primary-700 font-semibold hover:underline">
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
